import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, ShareNetwork, Ticket } from '@phosphor-icons/react';
import { FaFacebookF, FaTelegram, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { useParams } from 'react-router-dom';

import { API, BACKEND_URL } from '../context/AuthContext';

const BRAND = {
  background: '#081c24',
  panel: '#113945',
  accent: '#9f1d1e',
  gold: '#c4a768',
};

const FALLBACK_TRANSLATIONS = {
  tr: {
    title: 'Biletiniz Hazır!',
    download: 'Bileti İndir',
    share: 'Bileti Paylaş',
    status: 'Durum',
    email: 'E-posta',
    mode: 'Mod',
    generated: 'Oluşturuldu',
  },
  de: {
    title: 'Dein Ticket ist bereit',
    download: 'Bileti İndir',
    share: 'Bileti teilen',
    status: 'Status',
    email: 'E-Mail',
    mode: 'Modus',
    generated: 'Erzeugt',
  },
};

export function PublicTicketPage() {
  const { accessToken } = useParams();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedLocale, setSelectedLocale] = useState('tr');
  const [shareMessage, setShareMessage] = useState('');

  const resolveInitialLocale = (localeSettings) => {
    const supported = localeSettings?.supported_locales || ['tr', 'de'];
    if (supported.includes('tr')) return 'tr';
    return localeSettings?.default_locale || supported[0] || 'tr';
  };

  useEffect(() => {
    const fetchTicket = async () => {
      try {
        const { data } = await axios.get(`${API}/public/tickets/${accessToken}`);
        setTicket(data);
        setSelectedLocale(resolveInitialLocale(data.locale_settings));
      } catch (err) {
        setError(err.response?.data?.detail || 'Ticket konnte nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };
    fetchTicket();
  }, [accessToken]);

  const translations = ticket?.locale_settings?.translations || {};
  const supportedLocales = ticket?.locale_settings?.supported_locales || ['tr', 'de'];
  const t = (key) => translations?.[selectedLocale]?.[key] || FALLBACK_TRANSLATIONS[selectedLocale]?.[key] || FALLBACK_TRANSLATIONS.tr[key] || key;
//  const fileUrl = ticket?.public_file_url ? `${BACKEND_URL}${ticket.public_file_url}` : '';
//  const publicUrl = ticket?.public_view_url ? new URL(ticket.public_view_url, BACKEND_URL).toString() : '';
  const toPublicUrl = (urlOrPath) => {
  if (!urlOrPath) return '';
  try {
    const parsed = new URL(urlOrPath, window.location.origin);
    return `${window.location.origin}${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return '';
  }
};

  const fileUrl = ticket?.public_file_url ? toPublicUrl(ticket.public_file_url) : '';
  const publicUrl = ticket?.public_view_url ? toPublicUrl(ticket.public_view_url) : '';
  const handleNativeShare = async () => {
    if (!publicUrl) return;
    try {
      if (navigator.share && fileUrl) {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        const file = new File([blob], `${ticket.serial_number}.png`, { type: 'image/png' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: ticket.campaign_name || 'Hatıra Bileti', files: [file], url: publicUrl });
          setShareMessage(selectedLocale === 'tr' ? 'Bilet paylaşıldı.' : 'Ticket wurde geteilt.');
          return;
        }
        await navigator.share({ title: ticket.campaign_name || 'Hatıra Bileti', url: publicUrl });
        setShareMessage(selectedLocale === 'tr' ? 'Bağlantı paylaşıldı.' : 'Link wurde geteilt.');
        return;
      }
      await navigator.clipboard.writeText(publicUrl);
      setShareMessage(selectedLocale === 'tr' ? 'Bağlantı kopyalandı.' : 'Link wurde kopiert.');
    } catch {
      setShareMessage(selectedLocale === 'tr' ? 'Paylaşım şu anda mümkün değil.' : 'Teilen ist aktuell nicht möglich.');
    }
  };

  const shareOnPlatform = (platform) => {
    if (!publicUrl) return;
    const text = encodeURIComponent(`${ticket?.campaign_name || 'Hatıra Bileti'} ${ticket?.serial_number || ''}`.trim());
    const url = encodeURIComponent(publicUrl);
    const targets = {
      whatsapp: `https://wa.me/?text=${text}%20${url}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      x: `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      telegram: `https://t.me/share/url?url=${url}&text=${text}`,
    };
    window.open(targets[platform], '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: BRAND.background }}><div className="w-8 h-8 border-4 border-[#c4a768] border-t-transparent rounded-full animate-spin" /></div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: BRAND.background }}>
        <div className="max-w-lg w-full rounded-[30px] border border-white/10 bg-white/5 p-10 text-center text-white backdrop-blur-xl" data-testid="public-ticket-error-state">
          <Ticket className="w-12 h-12 text-[#c4a768] mx-auto" />
          <h1 className="mt-4 text-3xl font-black tracking-tight">{selectedLocale === 'tr' ? 'Bilet kullanılamıyor' : 'Ticket nicht verfügbar'}</h1>
          <p className="mt-2 text-sm text-zinc-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white" style={{ background: `radial-gradient(circle at top, #113945 0%, ${BRAND.background} 58%, #040d12 100%)` }} data-testid="public-ticket-page">
      <div className="px-4 py-10 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-7xl space-y-8">
          <div className="flex justify-end gap-2">
            {supportedLocales.map((locale) => (
              <button key={locale} type="button" onClick={() => setSelectedLocale(locale)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${selectedLocale === locale ? 'border-[#c4a768] bg-[#c4a768] text-[#0b202a]' : 'border-white/15 bg-white/5 text-white'}`} data-testid={`public-ticket-locale-${locale}`}>
                {locale.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="grid gap-8 lg:grid-cols-[320px_minmax(0,1fr)_320px] lg:items-start">
            <div className="rounded-[36px] border border-white/10 bg-[#123c56] p-4 shadow-[0_24px_80px_rgba(0,0,0,0.32)]">
              <div className="relative overflow-hidden rounded-[28px] border border-white/10 aspect-[3/5] bg-[linear-gradient(180deg,#184d7a_0%,#1c4061_36%,#5f665d_72%,#c4a768_100%)]" data-testid="public-ticket-event-art">
                <div className="absolute inset-x-0 top-0 h-20 bg-[#184d7a] opacity-90" aria-hidden="true" />
                <div className="absolute left-6 right-6 top-24 space-y-5 text-white">
                  <p className="text-sm uppercase tracking-[0.28em] text-white/70">Hatıra Bileti</p>
                  <h3 className="text-4xl font-black leading-none">{ticket?.campaign_name || 'Etkinlik'}</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm text-white/85">
                    <div className="rounded-2xl border border-white/15 bg-black/10 p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#f5e2b5]">Durum</p>
                      <p className="mt-2 font-semibold">{ticket?.status}</p>
                    </div>
                    <div className="rounded-2xl border border-white/15 bg-black/10 p-3">
                      <p className="text-xs uppercase tracking-[0.2em] text-[#f5e2b5]">Seri</p>
                      <p className="mt-2 font-semibold">{ticket?.serial_number}</p>
                    </div>
                  </div>
                </div>
                <div className="absolute left-[-10%] bottom-[-12%] h-44 w-44 rounded-full border border-white/10 opacity-30" aria-hidden="true" />
                <div className="absolute right-[-12%] top-[28%] h-52 w-52 rounded-full border border-white/10 opacity-20" aria-hidden="true" />
              </div>
            </div>

            <div className="rounded-[34px] border border-white/10 bg-[#102d38]/92 p-6 shadow-[0_32px_90px_rgba(0,0,0,0.4)] backdrop-blur-xl">
              {ticket?.status === 'generated' ? (
                <img src={fileUrl} alt={ticket.serial_number} className="w-full rounded-[24px] border border-white/10 bg-black/20 object-contain" data-testid="public-ticket-image" />
              ) : (
                <div className="min-h-[480px] flex items-center justify-center rounded-[24px] border border-dashed border-white/10 bg-white/[0.03] text-center p-8 text-zinc-300" data-testid="public-ticket-pending-state">
                  <div>
                    <Ticket className="w-12 h-12 text-[#c4a768] mx-auto" />
                    <p className="mt-4 font-semibold text-white">{selectedLocale === 'tr' ? 'Biletiniz henüz hazır değil.' : 'Dein Ticket ist noch nicht final erzeugt.'}</p>
                    <p className="mt-2 text-sm">{ticket?.status}</p>
                  </div>
                </div>
              )}
            </div>

            <aside className="rounded-[34px] border border-white/10 bg-[#102e38]/92 p-6 shadow-[0_32px_90px_rgba(0,0,0,0.4)] backdrop-blur-xl space-y-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#c4a768]">Hatıra Bileti</p>
                <h1 className="mt-3 text-4xl font-black tracking-tight text-white">{t('title')}</h1>
                <p className="mt-2 text-base text-[#d8dddd]">{ticket.campaign_name || 'Hatıra Bileti'}</p>
              </div>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-5 text-sm text-[#d8dddd] space-y-3">
                <p><span className="text-[#8fa4a9]">{t('status')}:</span> <span className="font-semibold text-white">{ticket.status}</span></p>
                <p><span className="text-[#8fa4a9]">{t('email')}:</span> <span className="font-semibold text-white">{ticket.email_status}</span></p>
                <p><span className="text-[#8fa4a9]">{t('mode')}:</span> <span className="font-semibold text-white">{ticket.test_mode ? 'Test' : 'Live'}</span></p>
                <p><span className="text-[#8fa4a9]">{t('generated')}:</span> <span className="font-semibold text-white">{ticket.generated_at ? new Date(ticket.generated_at).toLocaleString('de-DE') : '—'}</span></p>
                <p><span className="text-[#8fa4a9]">Seri No:</span> <span className="font-semibold text-white">{ticket.serial_number}</span></p>
              </div>

              {ticket.status === 'generated' && (
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-[20px] px-5 text-base font-semibold text-white" style={{ backgroundColor: BRAND.accent }} data-testid="public-ticket-download-button">
                  <Download className="w-5 h-5" />
                  {t('download')}
                </a>
              )}
              <button onClick={handleNativeShare} className="inline-flex h-14 w-full items-center justify-center gap-3 rounded-[20px] border border-white/10 bg-white/5 px-5 text-base font-semibold text-white hover:bg-white/10" data-testid="public-ticket-share-button">
                <ShareNetwork className="w-5 h-5" />
                {t('share')}
              </button>

              <div className="rounded-[24px] border border-white/10 bg-white/[0.04] p-4" data-testid="public-ticket-social-logos-panel">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-[#c4a768] mb-3">Social Media</p>
                <div className="flex flex-wrap gap-3">
                  <button type="button" onClick={() => shareOnPlatform('whatsapp')} className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366]/20 text-white border border-white/10" data-testid="public-ticket-share-whatsapp"><FaWhatsapp className="h-5 w-5" /></button>
                  <button type="button" onClick={() => shareOnPlatform('facebook')} className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1877F2]/20 text-white border border-white/10" data-testid="public-ticket-share-facebook"><FaFacebookF className="h-5 w-5" /></button>
                  <button type="button" onClick={() => shareOnPlatform('x')} className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white border border-white/10" data-testid="public-ticket-share-x"><FaXTwitter className="h-5 w-5" /></button>
                  <button type="button" onClick={() => shareOnPlatform('telegram')} className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#229ED9]/20 text-white border border-white/10" data-testid="public-ticket-share-telegram"><FaTelegram className="h-5 w-5" /></button>
                </div>
              </div>

              {shareMessage && <p className="rounded-[20px] border border-white/10 bg-white/[0.04] p-4 text-sm text-[#d6dfdf]" data-testid="public-ticket-share-feedback">{shareMessage}</p>}
            </aside>
          </div>

          <footer className="grid gap-4 sm:grid-cols-3 rounded-[28px] border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md" data-testid="public-ticket-legal-footer">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">Sorumlu Kurum</p>
              <p className="mt-2 text-sm text-white">{ticket?.legal_info?.organization_name || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">Kontakt</p>
              <p className="mt-2 text-sm text-white">{ticket?.legal_info?.contact_email || '—'}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#c4a768]">Hinweis</p>
              <p className="mt-2 text-sm text-white leading-relaxed">{selectedLocale === 'tr' ? 'Bu hatıra bileti kişisel kullanım içindir.' : 'Dieses Erinnerungs-Ticket ist für die persönliche Nutzung bestimmt.'}</p>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}
